/**
 * Emit hosts for the `general` analytics group, callable without a Rematch
 * `dispatch`. Functions lifted from `analyticsScribeModel`'s effects keep the
 * effect's own payload.
 */
import { USE_RECORDING_V2 } from '@/app-shell/flags/capture-flags';
import { getDemoModeForSession } from '@/composition/hosts/growth/demo-flow/getDemoModeForSession';
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  type NoteAutoRetryDecidedEventProps,
  type NoteFailureOverlayClearedEventProps,
} from '@/models/analytics/features/notes';
import { logger } from '@/utils/logger/logger';
import { SegmentActionName, SegmentObjectName } from '@heidi/analytics/events';
import { type GenerationMethod } from '@heidi/schema/ml-scribe';
import { type UpdateUserRequest } from '@heidi/workspace/contracts';

export function gotFirstNonEmptyTranscriptChunk({
  sessionId,
  isTabSharing,
}: {
  sessionId: string;
  isTabSharing?: boolean;
}) {
  logger.info({
    event: SegmentActionName.GotFirstNonEmptyTranscriptChunk,
    sessionId,
    isRecordingV2Enabled: USE_RECORDING_V2(),
    isTabSharing: isTabSharing,
  });

  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.GotFirstNonEmptyTranscriptChunk,
      sessionId,
      isRecordingV2Enabled: USE_RECORDING_V2(),
      isTabSharing: isTabSharing,
    },
  });
}

export function integrationReadyModalClosed(input: { stay?: boolean }) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: input.stay
        ? SegmentActionName.IntegrationReadyModalClosedAndStay
        : SegmentActionName.IntegrationReadyModalClosedAndBack,
    },
  });
}

export function markSessionAsContextOnly(sessionId?: string) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.SessionMarkedAsContextOnly,
    },
  });
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.SessionMarkedAsContextOnly,
    props: { session_id: sessionId },
  });
}

export function navigationDiscoveryCall() {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.NavigationDiscoveryCall,
    },
  });
}

export function navigationFranceCall() {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.NavigationFranceCall,
    },
  });
}

export function navigationGermanCall() {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.NavigationGermanCall,
    },
  });
}

export function navigationSpanishCall() {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.NavigationSpanishCall,
    },
  });
}

export function noteAutoRetryDecided(props: NoteAutoRetryDecidedEventProps) {
  track({
    event_name: TrackingEventName.NoteAutoRetryDecided,
    props,
    options: { amplitude: false },
  });
}

export function noteFailureOverlayCleared(props: NoteFailureOverlayClearedEventProps) {
  track({
    event_name: TrackingEventName.NoteFailureOverlayCleared,
    props,
    options: { amplitude: false },
  });
}

// settings
export function noteGenerationFinished({
  isSuccess,
  sessionId,
  documentId,
  totalDuration,
  durationBeforeWaitingUpload,
  durationWaitingToUpload,
  durationWaitingToStream,
  durationStreaming,
  durationCompletingStreaming,
  isRecordingV2Enabled,
  isTabSharing,
  error,
}: {
  isSuccess: boolean;
  sessionId: string;
  documentId?: string;
  totalDuration?: number;
  durationBeforeWaitingUpload?: number;
  durationWaitingToUpload?: number;
  durationWaitingToStream?: number;
  durationStreaming?: number;
  durationCompletingStreaming?: number;
  isRecordingV2Enabled?: boolean;
  isTabSharing?: boolean;
  error?: string;
}) {
  logger.info('Note generation finished', {
    event: 'note_generation_finished',
    isSuccess,
    sessionId,
    documentId,
    totalDuration,
    durationBeforeWaitingUpload,
    durationWaitingToUpload,
    durationWaitingToStream,
    durationStreaming,
    durationCompletingStreaming,
    error,
    isRecordingV2Enabled: USE_RECORDING_V2(),
    isTabSharing: isTabSharing,
  });
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.NoteGenerationEnd,
      sessionId,
      isSuccess,
      documentId,
      totalDuration,
      durationBeforeWaitingUpload,
      durationWaitingToUpload,
      durationWaitingToStream,
      durationStreaming,
      durationCompletingStreaming,
      isRecordingV2Enabled: isRecordingV2Enabled,
      isTabSharing: isTabSharing,
    },
  });
}

export function noteGenerationStart({
  sessionId,
  documentId,
  startTime,
  isRecordingV2Enabled,
  isTabSharing,
  is_pinned_template,
  is_starter_pack_template,
  generation_method,
}: {
  sessionId: string;
  documentId?: string;
  startTime: number;
  isRecordingV2Enabled?: boolean;
  isTabSharing?: boolean;
  is_pinned_template: boolean;
  is_starter_pack_template: boolean;
  generation_method?: GenerationMethod | null;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.NoteGenerationStart,
      sessionId,
      documentId,
      noteGenerationStartTime: startTime,
      isRecordingV2Enabled: isRecordingV2Enabled,
      isTabSharing: isTabSharing,
      is_pinned_template,
      is_starter_pack_template,
    },
  });

  // V2 — Amplitude + Datadog only
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.NoteStarted,
    props: {
      session_id: sessionId,
      document_id: documentId,
      start_time_ms: startTime,
      is_recording_v2_enabled: isRecordingV2Enabled,
      is_tab_sharing: isTabSharing,
      is_pinned_template,
      is_starter_pack_template,
      generation_method,
      // tag note_started when generation runs for the demo
      // session. Fires for both the natural-end and skip-to-results paths.
      demo_mode: getDemoModeForSession(sessionId),
    },
  });
}

export function saveSettings(settingsUpdatedData: UpdateUserRequest) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.SaveSettings,
      settingsUpdatedData,
    },
  });
}

export function tauriSessionMismatch() {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.TAURI_SESSION_MISMATCH,
    },
  });
}

export function trackBackupSizeZeroError(input: { sessionId: string }) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.BackupSizeZeroError,
      sessionId: input.sessionId,
    },
  });
}

export function trackChunkSessionMismatch(input: {
  currentSessionId: string;
  newSessionId: string;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.SessionChunkMismatchError,
      currentSessionId: input.currentSessionId,
      newSessionId: input.newSessionId,
    },
  });
}

export function trackDefaultDeviceErrorTauri() {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.TAURI_DEFAULT_DEVICE_ERROR,
    },
  });
}

export function trackDeleteAccountSuccess(input: {
  userId?: string;
  reason?: string;
  details?: string;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.DeleteAccountSuccess,
      userId: input.userId,
      reason: input.reason,
      details: input.details,
    },
  });

  // V2 — Amplitude migration
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.AccountDeleted,
    props: {
      reason: input.reason,
      details: input.details,
    },
  });
}

export function trackFailedToSendChunk(input: {
  sessionId: string;
  recordingId: string;
  chunkId: number;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.FailedToSendChunk,
      sessionId: input.sessionId,
      recordingId: input.recordingId,
      chunkId: input.chunkId,
    },
  });
}

export function trackFailedToStartChunkRecording(error?: string) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.FailedToStartChunkRecording,
      error: error || '',
    },
  });
}

export function trackGetCurrentRecordingFailed({
  speakerSize,
  audioSize,
}: {
  speakerSize?: number;
  audioSize?: number;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.TAURI_GET_RECORDING_FAILED,
      speakerSize: speakerSize,
      audioSize: audioSize,
    },
  });
}

export function trackMediaRecorderFailedToStart(input: {
  sessionId: string;
  recordingId: string;
  chunkCount: number;
  error: string;
}) {
  const { sessionId, recordingId, chunkCount, error } = input;
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.FailedToStartMediaRecorderChunk,
      sessionId: sessionId,
      recordingId: recordingId,
      chunkCount: chunkCount,
      error: error,
    },
  });
}

export function trackMediaRecorderUnableToStop() {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.MediaRecorderUnableToStop,
    },
  });
}

export function trackMediaStreamChunkTrackStateError(input: { error: string }) {
  const { error } = input;
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.FailedTrackStatusInChunk,
      error: error,
    },
  });
}

export function trackRecordingTriesToStartWhenStatusIsRecording() {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.RecordingTriesToStartWhenStatusIsRecording,
    },
  });
}

export function trackSnippetOpenPrompt() {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.OpenSnippetPrompt,
    },
  });
}

export function trackSnippetUsed(input: {
  snippetId: string;
  contentLength: number;
  shorthandLength: number;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.UseSnippet,
      snippetId: input.snippetId,
      contentLength: input.contentLength,
      shorthandLength: input.shorthandLength,
    },
  });
}

export function trackToggleUserMfa(input: { enabled: boolean }) {
  trackLegacyEvent({
    name: SegmentObjectName.General,
    props: {
      action: SegmentActionName.UserMfaToggle,
      enabled: input.enabled,
    },
  });
}
